import store from "@/store"
import i18n from "@/locale/index"
import { CURRENCY_OPTIONS, TEMP_OPTIONS } from "@/utils/options"
export const optionalChaining = (obj, ...rest) => {
  let tmp = obj
  for (let key in rest) {
    let name = rest[key]
    tmp = tmp?.[name]
  }
  return tmp ?? null
}

export const withUnits = (val, filter, ...args) => {
  const filters = {
    withUnit,
    numToFixed,
    WITH_COMMAS,
    PERCENT_UNIT,
    TEMP_UNIT,
    CURRENCY_UNIT,
    DAY_UNIT,
    YEAR_UNIT,
    HZ_UNIT,
    CV_UNIT,
    L_Day_UNIT,
    ENERGY_UNIT,
    TARIFF_UNIT,
    TARIFF_kWh,
    kg_CO2_UNIT,
    CAPACITY_UNIT,
    LOAD_UNIT,
    EFFICIENCY_UNIT,
    kWh_UNIT,
    kW_UNIT
  }

  if (filters[filter]) {
    return filters[filter](val, ...args)
  } else {
    console.warn(`Filter "${filter}" not found, returning original value`)
    return val
  }
}

// type:0-unit only; 1- number with unit, eg:【31 days】 ； 2- string with unit in (), eg: 【total time(days)】
export const withUnit = (val, unit, type = 1, isCurrency = false) => {
  if (!unit) return val
  if (!type) return unit
  if (isValEmpty(val, 0)) return "--"
  if (type && isNaN(val)) return `${val} (${unit})`
  if (isCurrency) return `${unit} ${WITH_COMMAS(val)} `
  if (isNumeric(val)) return `${WITH_COMMAS(val)} ${unit}`
  return `${val} (${unit})`
}

// let data = this.$options.filters.WITH_COMMAS(10)
// let temp = Vue.filters("WITH_COMMAS")(10000)
export const WITH_COMMAS = num => {
  return (+num || 0).toString().replace(/^-?\d+/g, m => m.replace(/(?=(?!\b)(\d{3})+$)/g, ","))
}

export const PERCENT_UNIT = (val, digits = 1) => {
  if (digits === -1 && isNaN(val)) return `${val}(%)`
  val = numToFixed(val, digits)
  return isNumeric(val) ? `${val}%` : "--"
}
export const TEMP_UNIT = (val, type = 1) => {
  const unit = store.getters["projectPre/tempUnit"].name || TEMP_OPTIONS[0].name
  return withUnit(val, unit, type)
}

export const CURRENCY_UNIT = (val, currency = -1) => {
  const unit = currency < 0 ? store.getters["projectPre/currency"] : CURRENCY_OPTIONS.find(x => x.value === currency) || CURRENCY_OPTIONS[0]
  return withUnit(val, unit.symbol, 1, 1)
}
export const HOUR_UNIT = (val = 0, type = 1) => {
  const unit = i18n.t("index").label[["Hour", "Hours"][+(val > 1)]]
  return withUnit(val, unit, type)
}
export const DAY_UNIT = (val = 0, type = 1) => {
  const unit = i18n.t("index").label[["Day", "Days"][+(val > 1)]]
  return withUnit(val, unit, type)
}
export const YEAR_UNIT = (val = 0, type = 1) => {
  const unit = i18n.t("index").label[["Year", "Years"][+(val > 1)]]
  return withUnit(val, unit, type)
}

export const HZ_UNIT = (val, type = 1) => {
  return withUnit(val, "Hz", type)
}
export const kW_UNIT = (val, type = 1) => {
  return withUnit(val, "kW", type)
}
export const kWh_UNIT = (val, type = 1) => {
  return withUnit(val, "kWh", type)
}
export const CV_UNIT = (val, type = 1) => {
  const unit = store.getters["projectPre/energyType"]?.unit || "kWh"
  return withUnit(val, `MJ / ${i18n.t("index").label[unit] || unit}`, type)
}

export const L_Day_UNIT = (val, type = 1) => {
  const unit = `${i18n.t("index").label.liters} / ${i18n.t("index").label.Day.toLowerCase()}`
  return withUnit(val, unit, type)
}

export const ENERGY_UNIT = (val, type = 1) => {
  const unit = store.getters["projectPre/energyType"]?.unit
  return withUnit(val, i18n.t("index").label[unit] || unit, type)
}

export const TARIFF_UNIT = (val, type = 1) => {
  const currency = store.getters["projectPre/currency"]?.symbol || "$"
  const unit = store.getters["projectPre/energyType"]?.unit || "kWh"
  const note = i18n.t("index").label[unit] || unit
  return withUnit(val, `${currency} / ${note}`, type)
}

export const TARIFF_kWh = (val, type = 1) => {
  const currency = store.getters["projectPre/currency"]?.symbol
  return withUnit(val, `${currency} / kWh`, type)
}

export const kg_CO2_UNIT = (val, type = 1) => {
  const unit = "kgCO₂e / kWh"
  return withUnit(val, unit, type)
}

export const CAPACITY_UNIT = (val, type = 1) => {
  const unit = store.getters["projectPre/capacityUnit"].unit
  return withUnit(val, unit, type)
}

export const LOAD_UNIT = (val, type = 1) => {
  const unit = store.getters["projectPre/capacityUnit"].load
  return withUnit(val, unit, type)
}

export const EFFICIENCY_UNIT = (val, type = 1) => {
  const unit = store.getters["projectPre/capacityUnit"].efficiency
  return withUnit(val, unit, type)
}

export const formatName = name => {
  if (!name || typeof name !== "string") return ""
  return name
    .split("@")[0]
    .split(".")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

export const formatCase = (text, type = 1) => {
  if (typeof text !== "string") return text
  switch (type) {
    case 0:
      return text.toLowerCase()
    case 1:
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
    case 2:
      return text
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ")
    case 3:
      return text.toUpperCase()
    default:
      return text
  }
}

export const formatJoinText = (texts = [], type = 1) => {
  const validTexts = texts.filter(t => !isValEmpty(t))
  if (validTexts.length === 0) return ""
  if (validTexts.length === 1) return formatCase(validTexts[0], type)
  const currentLocale = i18n.locale || "en"
  const noSpace = ["zh"].includes(currentLocale)
  const separator = noSpace ? "" : " "
  return formatCase(validTexts.join(separator), type)
}

export const formatDateTime = (data, format = "yyyy-mm-dd") => {
  let date = typeof data === "number" ? new Date(data) : data
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const day = date.getDate().toString().padStart(2, "0")
  const hour = date.getHours().toString().padStart(2, "0")
  const minute = date.getMinutes().toString().padStart(2, "0")
  const second = date.getSeconds().toString().padStart(2, "0")

  return format.replace("yyyy", year).replace("mm", month).replace("dd", day).replace("hh", hour).replace("MM", minute).replace("ss", second)
}

export const numToFixed = (num, digits = 0) => {
  if (Array.isArray(num)) return num.map(item => numToFixed(item, digits))
  if (Object.prototype.toString.call(num) === "[object Object]") {
    return Object.keys(num).reduce((result, key) => {
      result[key] = numToFixed(num[key], digits)
      return result
    }, {})
  }
  if (!isFinite(num)) return num
  if (!isNumeric(num)) return num
  const strNum = num.toString()
  const decimalPart = strNum.split(".")[1]
  return !decimalPart || decimalPart.length <= digits ? num : parseFloat(num.toFixed(digits))
}

export const formatNum = (num, digits, unit) => {
  let si = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "B" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "P" },
    { value: 1e18, symbol: "E" }
  ]
  if (!!unit) si = si.filter(x => x.symbol === unit)

  let i
  for (i = si.length - 1; i > 0; i--) {
    if (num >= si[i].value) {
      break
    }
  }

  let value = 0
  while (num && !value) {
    value = (num / si[i].value).toFixed(digits) * 1
    digits++
  }
  // const rx = /\.0+$|(\.[0-9]*[1-9])0+$/
  // const value = temp.replace(rx, "$1")
  return {
    value,
    unit: si[i].symbol,
    str: `${value}${si[i].symbol}`
  }
}

export const roundNumStep = (num, step = 5) => {
  const strNum = Math.ceil(Math.abs(num)).toString()
  const len = strNum.length
  if (len < 2) return Math.ceil(num / step) * step

  let firstTwo = parseInt(strNum.substring(0, 2)) + 2
  firstTwo = Math.ceil(firstTwo / step) * step
  return parseInt(firstTwo.toString() + "0".repeat(len - 2))
}

export const isNumeric = value => {
  if (typeof value === "number" && !isNaN(value) && value !== Infinity) return true

  if (typeof value === "string") {
    if (value.trim() === "") return false
    return /^[-+]?(\d+\.?\d*|\.\d+)$/.test(value.trim())
  }
  return false
}

export const isValEmpty = (value, nonZero = true) => {
  return value === null || value === undefined || value === "" || value === Infinity || (nonZero && value === 0)
}

export const isArrHasEmpty = (arr, nonZero = true) => {
  if (!Array.isArray(arr) || arr.length === 0) return true
  return arr.some(x => isValEmpty(x, nonZero))
}

export const isArrAllEmpty = (arr, nonZero = true) => {
  if (!Array.isArray(arr) || arr.length === 0) return true
  return arr.every(x => isValEmpty(x, nonZero))
}

export const isObjectEmpty = obj => {
  if (typeof obj !== "object" || obj === null) return !obj

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key]

      if (Array.isArray(value))
        if (value.length === 0) continue
        else return false
      if (typeof value === "object" && value !== null) {
        if (!isObjectEmpty(value)) return false
      } else if (value !== null && value !== undefined && value !== "") {
        return false
      }
    }
  }
  return true
}
export const deepMerge = (target, source) => {
  if (typeof target !== "object" || target === null || typeof source !== "object" || source === null) {
    return source
  }
  if (source instanceof Date || source instanceof RegExp) {
    return source
  }

  const merged = Array.isArray(target) ? [...target] : { ...target }

  for (const key of Reflect.ownKeys(source)) {
    if (Object.hasOwn(source, key)) {
      const sourceVal = source[key]
      const targetVal = merged[key]

      if (Array.isArray(sourceVal) && Array.isArray(targetVal)) {
        merged[key] = [...sourceVal]
      } else if (typeof sourceVal === "object" && sourceVal !== null) {
        merged[key] = deepMerge(targetVal, sourceVal)
      } else {
        merged[key] = sourceVal
      }
    }
  }

  return merged
}

// param example：
// 1) "eqptNo"
// 2) "digitalECATData.series"
// 3) "clusters[0].motor"
export const getValByPath = (obj, param, defaultValue = undefined) => {
  if (obj == null || !param) return defaultValue

  const keys = String(param)
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .filter(Boolean)

  let cur = obj
  for (const key of keys) {
    if (cur == null) return defaultValue
    cur = cur[key]
  }
  return cur === undefined ? defaultValue : cur
}



// WEBPACK FOOTER //
// ./src/utils/filters.js